import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import http from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { TERMS_VERSION } from "@/pages/Vilkar";
import { useLive } from "@/lib/serviceInfo";

const TOTAL_STEPS = 5;
const symptomOptions = [
  "Maskinen starter ikke", "Lager ikke kaffe", "Lekker vann", "Lekker kaffe", "Varmer ikke",
  "For lite / ingen kaffe", "Kaffen er kald", "Kaffen er for svak / for sterk", "Uvanlig lyd",
  "Stopper under bruk", "Kvern fungerer ikke", "Problem med melkesystem / damp",
  "Krever rengjøring / avkalking", "Utsatt for væske", "Utsatt for støt eller fall", "Feilmelding på display", "Annet",
];
const whenOccursOptions = ["Ved oppstart", "Under bruk", "Etter bruk", "Sporadisk", "Nylig begynt"];
const accessoryOptions = [
  ["vanntank", "Vanntank"], ["dryppbrett", "Dryppbrett"], ["bryggeenhet", "Bryggeenhet"],
  ["melkesystem", "Melkesystem / dampenhet"], ["stromkabel", "Strømkabel"],
];

export default function Innlevering() {
  const live = useLive();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackCode, setTrackCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [language, setLanguage] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [machineBrand, setMachineBrand] = useState("");
  const [machineModel, setMachineModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [machineAge, setMachineAge] = useState("");
  const [waterConnection, setWaterConnection] = useState("");
  const [accessories, setAccessories] = useState({});
  const [deliveredComplete, setDeliveredComplete] = useState(false);
  const [visibleDamage, setVisibleDamage] = useState("");
  const [faultDescription, setFaultDescription] = useState("");
  const [faultSymptoms, setFaultSymptoms] = useState([]);
  const [faultCode, setFaultCode] = useState("");
  const [whenOccurs, setWhenOccurs] = useState("");
  const [wantsPriceEstimate, setWantsPriceEstimate] = useState("yes");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);
  const [priority, setPriority] = useState("normal");

  const isOldMachine = ["9-10 år", "Over 10 år"].includes(machineAge);

  const canProceed = () => {
    switch (step) {
      case 1: return customerName.trim() && customerPhone.trim() && customerEmail.trim() && address.trim() && postalCode.trim() && city.trim();
      case 2: return machineBrand.trim() && machineModel.trim() && machineAge && waterConnection;
      case 3: return faultDescription.trim() && faultSymptoms.length > 0 && whenOccurs;
      case 4: return selectedPackage !== "";
      case 5: return termsAccepted;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!termsAccepted) return;
    setSubmitting(true);
    try {
      const res = await http.post("/v1/submissions", {
        customer_name: customerName, customer_phone: customerPhone, customer_email: customerEmail,
        address, postal_code: postalCode, city,
        machine_brand: machineBrand, machine_model: machineModel, serial_number: serialNumber,
        machine_age: machineAge, water_connection: waterConnection,
        accessories, delivered_complete: deliveredComplete, visible_damage: visibleDamage,
        fault_description: faultDescription, fault_symptoms: faultSymptoms.join(", "),
        fault_code: faultCode, when_occurs: whenOccurs,
        wants_price_estimate: wantsPriceEstimate === "yes",
        selected_package: selectedPackage, terms_accepted: true, terms_version: TERMS_VERSION,
        status: "pending", is_business: isBusiness, priority, preferred_language: language,
      });
      setTrackCode(res.data?.order_number || "");
      setEmailSent(!!res.data?.email_sent);
      setSubmitted(true);
      toast.success("Innleveringsskjema sendt!");
    } catch {
      toast.error("Noe gikk galt. Vennligst prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSymptom = (s) => setFaultSymptoms((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  const toggleAccessory = (k) => setAccessories((prev) => ({ ...prev, [k]: !prev[k] }));

  if (submitted) {
    return (
      <Layout>
        <section className="py-24 bg-[#FFF8F0]">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6"><CheckCircle className="h-8 w-8 text-green-600" /></div>
            <h1 className="text-2xl font-bold text-[#3D2314] mb-4 font-display">Takk for innleveringen!</h1>
            <p className="text-[#1A1A1A]/70 mb-6">Vi har mottatt skjemaet ditt og tar kontakt innen én virkedag. Du kan nå levere maskinen til verkstedet vårt i Birkeveien 2, 5093 Bergen.</p>
            {trackCode && (
              <div className="bg-[#D4A574]/15 border border-[#D4A574]/40 rounded-lg p-4 mb-6" data-testid="track-code-box">
                <p className="text-sm text-[#1A1A1A]/70 mb-1">Din sporingskode:</p>
                <p className="text-2xl font-bold text-[#3D2314] font-display">{trackCode}</p>
                <p className="text-xs text-[#1A1A1A]/60 mt-2">Oppgi denne koden når du kontakter oss eller spør Vadim om status.{emailSent ? " Du har også fått en e-post med koden." : ""}</p>
              </div>
            )}
            <div className="bg-white rounded-lg p-4 text-sm text-left space-y-1 mb-6"><p><strong>Åpningstider for innlevering:</strong></p><p>Mandag–Fredag: {live.hoursWeekday}</p></div>
            <Button asChild className="bg-[#3D2314] hover:bg-[#3D2314]/90 text-white"><Link to="/">Tilbake til forsiden</Link></Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-12 md:py-16 bg-[#FFF8F0]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#3D2314]">Steg {step} av {TOTAL_STEPS}</span>
              <span className="text-sm text-[#1A1A1A]/60">{["Kontaktinfo", "Maskininfo", "Feilbeskrivelse", "Velg pakke", "Bekreft og send"][step - 1]}</span>
            </div>
            <div className="w-full bg-[#D4A574]/20 rounded-full h-2"><div className="bg-[#D4A574] h-2 rounded-full transition-all duration-300" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} /></div>
          </div>

          <Card className="border-[#D4A574]/20">
            <CardHeader><CardTitle className="text-xl text-[#3D2314]">{["Dine kontaktopplysninger", "Om maskinen din", "Beskriv feilen", "Velg servicepakke", "Bekreft og send inn"][step - 1]}</CardTitle></CardHeader>
            <CardContent>
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="lang">Språk / Язык / Language</Label>
                    <select id="lang" data-testid="inn-language" value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full rounded-md border border-[#D4A574]/40 bg-white px-3 py-2 text-sm text-[#3D2314]">
                      <option value="">Velg språk (valgfritt)</option>
                      <option value="no">Norsk</option>
                      <option value="en">English</option>
                      <option value="ru">Русский</option>
                      <option value="uk">Українська</option>
                      <option value="pl">Polski</option>
                      <option value="lt">Lietuvių</option>
                      <option value="de">Deutsch</option>
                      <option value="sv">Svenska</option>
                      <option value="da">Dansk</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                    </select>
                    <p className="text-xs text-[#1A1A1A]/50 mt-1">Vi sender bekreftelser og statusoppdateringer på dette språket. Uten valg brukes språket vi allerede har registrert for deg.</p>
                  </div>
                  <div><Label htmlFor="name">Fullt navn *</Label><Input id="name" data-testid="inn-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Ola Nordmann" className="mt-1" /></div>
                  <div><Label htmlFor="phone">Telefonnummer *</Label><Input id="phone" data-testid="inn-phone" type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="912 34 567" className="mt-1" /></div>
                  <div><Label htmlFor="email">E-postadresse *</Label><Input id="email" data-testid="inn-email" type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="ola@eksempel.no" className="mt-1" /></div>
                  <div><Label htmlFor="address">Adresse *</Label><Input id="address" data-testid="inn-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Gate og husnummer" className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="postal">Postnummer *</Label><Input id="postal" data-testid="inn-postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="5093" className="mt-1" /></div>
                    <div><Label htmlFor="city">Sted *</Label><Input id="city" data-testid="inn-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Bergen" className="mt-1" /></div>
                  </div>
                  <div className="rounded-lg border border-[#D4A574]/30 bg-[#FFF8F0] p-4 space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="inn-business">
                      <input type="checkbox" checked={isBusiness} onChange={(e) => { setIsBusiness(e.target.checked); if (!e.target.checked) setPriority("normal"); }} className="h-4 w-4 accent-[#3D2314]" />
                      <span className="text-sm font-medium text-[#3D2314]">Jeg leverer inn som bedriftskunde</span>
                    </label>
                    {isBusiness && (
                      <label className="flex items-center gap-3 cursor-pointer" data-testid="inn-urgent">
                        <input type="checkbox" checked={priority === "akutt"} onChange={(e) => setPriority(e.target.checked ? "akutt" : "normal")} className="h-4 w-4 accent-red-600" />
                        <span className="text-sm font-medium text-[#3D2314]">Haster / utrykning – prioritert behandling</span>
                      </label>
                    )}
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4">
                  <div><Label htmlFor="brand">Merke *</Label><Input id="brand" data-testid="inn-brand" value={machineBrand} onChange={(e) => setMachineBrand(e.target.value)} placeholder="f.eks. DeLonghi, Jura, Miele" className="mt-1" /></div>
                  <div><Label htmlFor="model">Modell *</Label><Input id="model" data-testid="inn-model" value={machineModel} onChange={(e) => setMachineModel(e.target.value)} placeholder="f.eks. Magnifica S, E8" className="mt-1" /></div>
                  <div><Label htmlFor="serial">Serienummer (hvis kjent)</Label><Input id="serial" data-testid="inn-serial" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="Valgfritt" className="mt-1" /></div>
                  <div>
                    <Label>Alder på maskinen (år) *</Label>
                    <Select value={machineAge} onValueChange={setMachineAge}>
                      <SelectTrigger className="mt-1" data-testid="inn-age"><SelectValue placeholder="Velg alder" /></SelectTrigger>
                      <SelectContent>{["Under 1 år", "1-2 år", "3-4 år", "5-6 år", "7-8 år", "9-10 år", "Over 10 år"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                    {isOldMachine && <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg"><AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" /><p className="text-sm text-amber-800">For maskiner over 9 år anbefaler vi Pakke A (diagnostikk) først, for å vurdere om reparasjon er lønnsomt.</p></div>}
                  </div>
                  <div>
                    <Label>Vanntilkobling *</Label>
                    <RadioGroup value={waterConnection} onValueChange={setWaterConnection} className="mt-2">
                      {[["tank", "Vanntank (manuell fylling)"], ["direct", "Fast vanntilkobling"], ["unknown", "Vet ikke"]].map(([v, l]) => (
                        <div key={v} className="flex items-center space-x-2"><RadioGroupItem value={v} id={v} /><Label htmlFor={v} className="font-normal">{l}</Label></div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label>Medfølgende tilbehør</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {accessoryOptions.map(([k, l]) => (
                        <div key={k} className="flex items-center space-x-2"><Checkbox id={`acc-${k}`} data-testid={`inn-acc-${k}`} checked={!!accessories[k]} onCheckedChange={() => toggleAccessory(k)} /><Label htmlFor={`acc-${k}`} className="font-normal text-sm">{l}</Label></div>
                      ))}
                    </div>
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <Checkbox id="complete" data-testid="inn-complete" checked={deliveredComplete} onCheckedChange={(c) => setDeliveredComplete(c === true)} />
                      <span className="text-sm text-[#1A1A1A]/80">Jeg bekrefter at maskinen leveres komplett med relevante deler (vanntank, dryppbrett, bryggeenhet, melkesystem der aktuelt).</span>
                    </label>
                  </div>
                  <div><Label htmlFor="damage">Synlige skader ved innlevering</Label><Textarea id="damage" data-testid="inn-damage" value={visibleDamage} onChange={(e) => setVisibleDamage(e.target.value)} placeholder="Beskriv ev. riper, sprekker eller manglende deler (valgfritt)" className="mt-1 min-h-[70px]" /></div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label>Hvilke symptomer opplever du? *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {symptomOptions.map((s) => <div key={s} className="flex items-center space-x-2"><Checkbox id={s} checked={faultSymptoms.includes(s)} onCheckedChange={() => toggleSymptom(s)} /><Label htmlFor={s} className="font-normal text-sm">{s}</Label></div>)}
                    </div>
                  </div>
                  <div><Label htmlFor="faultcode">Feilmelding / feilkode (hvis vist)</Label><Input id="faultcode" data-testid="inn-faultcode" value={faultCode} onChange={(e) => setFaultCode(e.target.value)} placeholder="f.eks. E8, «fyll vann»" className="mt-1" /></div>
                  <div>
                    <Label>Når oppstår feilen? *</Label>
                    <RadioGroup value={whenOccurs} onValueChange={setWhenOccurs} className="mt-2 space-y-1">
                      {whenOccursOptions.map((o) => <div key={o} className="flex items-center space-x-2"><RadioGroupItem value={o} id={`when-${o}`} /><Label htmlFor={`when-${o}`} className="font-normal text-sm">{o}</Label></div>)}
                    </RadioGroup>
                  </div>
                  <div><Label htmlFor="description">Beskriv feilen med egne ord *</Label><Textarea id="description" data-testid="inn-description" value={faultDescription} onChange={(e) => setFaultDescription(e.target.value)} placeholder="Fortell oss hva som skjer med maskinen din..." className="mt-1 min-h-[100px]" /></div>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-4">
                  {isOldMachine && <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4"><AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" /><p className="text-sm text-amber-800">Basert på maskinens alder anbefaler vi å starte med Pakke A for å vurdere om reparasjon er lønnsomt.</p></div>}
                  <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="space-y-3">
                    {[["A", "Pakke A – Standard diagnostikk", "Feilsøking, tilstandsrapport og prisoverslag", `${live.priceA} inkl. mva · inntil 2 arbeidstimer`], ["B", "Pakke B – Forebyggende service", "Full service inkl. rengjøring og bytte av slitasjedeler", `fra ${live.priceB}`], ["C", "Pakke C – Utvidet diagnostikk", "Dyptgående analyse og komponentnivå-reparasjon", `fra ${live.priceC}/dag`]].map(([v, title, desc, price]) => (
                      <label key={v} htmlFor={`pkg-${v}`} data-testid={`inn-pkg-${v}`} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${selectedPackage === v ? "border-[#D4A574] bg-[#D4A574]/5" : "border-gray-200 hover:border-[#D4A574]/50"}`}>
                        <RadioGroupItem value={v} id={`pkg-${v}`} className="mt-1" />
                        <div className="flex-1"><div className="flex justify-between items-start"><div><p className="font-semibold text-[#3D2314]">{title}</p><p className="text-sm text-[#1A1A1A]/60 mt-1">{desc}</p></div><span className="font-bold text-[#3D2314] whitespace-nowrap">{price}</span></div></div>
                      </label>
                    ))}
                  </RadioGroup>
                  <div className="rounded-lg border border-[#D4A574]/30 bg-[#FFF8F0] p-4">
                    <Label className="text-sm">Ønsker du prisoverslag før reparasjon?</Label>
                    <RadioGroup value={wantsPriceEstimate} onValueChange={setWantsPriceEstimate} className="mt-2 flex gap-6">
                      <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="pe-yes" /><Label htmlFor="pe-yes" className="font-normal text-sm">Ja, jeg ønsker prisoverslag</Label></div>
                      <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="pe-no" /><Label htmlFor="pe-no" className="font-normal text-sm">Nei, ikke nødvendig</Label></div>
                    </RadioGroup>
                  </div>
                  <p className="text-xs text-[#1A1A1A]/60">Pakke A har fast pris (inntil 2 arbeidstimer). Prisene for Pakke B og C er veiledende «fra-priser». Endelig pris for videre arbeid bekreftes etter diagnostikk, og deler kommer i tillegg.</p>
                </div>
              )}
              {step === 5 && (
                <div className="space-y-6">
                  <div className="bg-[#FFF8F0] rounded-lg p-4 space-y-2 text-sm">
                    <h3 className="font-semibold text-[#3D2314] mb-3">Oppsummering</h3>
                    <p><span className="text-[#1A1A1A]/60">Navn:</span> {customerName}</p>
                    <p><span className="text-[#1A1A1A]/60">Telefon:</span> {customerPhone}</p>
                    <p><span className="text-[#1A1A1A]/60">E-post:</span> {customerEmail}</p>
                    <p><span className="text-[#1A1A1A]/60">Adresse:</span> {address}, {postalCode} {city}</p>
                    <p><span className="text-[#1A1A1A]/60">Maskin:</span> {machineBrand} {machineModel}{serialNumber ? ` (S/N: ${serialNumber})` : ""}</p>
                    <p><span className="text-[#1A1A1A]/60">Alder:</span> {machineAge}</p>
                    <p><span className="text-[#1A1A1A]/60">Valgt pakke:</span> Pakke {selectedPackage}</p>
                    <p><span className="text-[#1A1A1A]/60">Symptomer:</span> {faultSymptoms.join(", ")}</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox id="terms" data-testid="inn-terms" checked={termsAccepted} onCheckedChange={(c) => setTermsAccepted(c === true)} />
                    <Label htmlFor="terms" className="font-normal text-sm leading-relaxed">Jeg bekrefter at jeg har lest og akseptert <Link to="/vilkar" className="text-[#D4A574] underline hover:text-[#3D2314]" target="_blank">vilkårene og garantibetingelsene</Link>. Jeg forstår at all reparasjon starter med betalt diagnostikk, at endelig pris bekreftes etter diagnostikk, at deler kommer i tillegg, og at garantien kun gjelder den konkrete, dokumenterte feilen som utbedres. *</Label>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-8 pt-4 border-t">
                {step > 1 ? <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-2"><ArrowLeft className="h-4 w-4" />Tilbake</Button> : <div />}
                {step < TOTAL_STEPS ? (
                  <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} data-testid="inn-next" className="gap-2 bg-[#3D2314] hover:bg-[#3D2314]/90 text-white">Neste<ArrowRight className="h-4 w-4" /></Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={!canProceed() || submitting} data-testid="inn-submit" className="gap-2 bg-[#D4A574] hover:bg-[#D4A574]/90 text-[#3D2314] font-semibold">{submitting ? "Sender..." : "Send inn skjema"}{!submitting && <CheckCircle className="h-4 w-4" />}</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
